import React from "react";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";

interface PaginationControlsProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    maxVisiblePages?: number;
}

export function PaginationControls({
    currentPage,
    totalPages,
    onPageChange,
    maxVisiblePages = 7,
}: PaginationControlsProps) {
    // Don't render pagination if there's only one page or no pages
    if (totalPages <= 1) {
        return null;
    }

    const getVisiblePages = () => {
        const pages: (number | "ellipsis")[] = [];

        if (totalPages <= maxVisiblePages) {
            // If total pages is less than max visible, show all pages
            for (let i = 0; i < totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(0);

            let start = Math.max(1, currentPage - Math.floor((maxVisiblePages - 3) / 2));
            let end = Math.min(totalPages - 2, currentPage + Math.floor((maxVisiblePages - 3) / 2));

            // Adjust start and end to ensure we show exactly maxVisiblePages - 2 pages in the middle
            if (end - start + 1 < maxVisiblePages - 3) {
                if (start === 1) {
                    end = Math.min(totalPages - 2, start + maxVisiblePages - 4);
                } else {
                    start = Math.max(1, end - maxVisiblePages + 4);
                }
            }

            // Add ellipsis before middle pages if needed
            if (start > 1) {
                pages.push("ellipsis");
            }

            // Add middle pages
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            // Add ellipsis after middle pages if needed
            if (end < totalPages - 2) {
                pages.push("ellipsis");
            }

            // Always show last page
            pages.push(totalPages - 1);
        }

        return pages;
    };

    const visiblePages = getVisiblePages();

    return (
        <Pagination>
            <PaginationContent>
                <PaginationItem>
                    <PaginationPrevious
                        onClick={() => onPageChange(currentPage - 1)}
                        className={
                            currentPage === 0 ? "pointer-events-none opacity-50" : "cursor-pointer"
                        }
                    />
                </PaginationItem>

                {visiblePages.map((page, index) => (
                    <PaginationItem key={index}>
                        {page === "ellipsis" ? (
                            <PaginationEllipsis />
                        ) : (
                            <PaginationLink
                                onClick={() => onPageChange(page)}
                                isActive={currentPage === page}
                                className="cursor-pointer"
                            >
                                {page + 1}
                            </PaginationLink>
                        )}
                    </PaginationItem>
                ))}

                <PaginationItem>
                    <PaginationNext
                        onClick={() => onPageChange(currentPage + 1)}
                        className={
                            currentPage >= totalPages - 1
                                ? "pointer-events-none opacity-50"
                                : "cursor-pointer"
                        }
                    />
                </PaginationItem>
            </PaginationContent>
        </Pagination>
    );
}
